import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Save, AlignLeft, AlignCenter, AlignRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import apiClient from '@/utils/api';

export default function AboutBulokeSettings() {
  const [settings, setSettings] = useState({
    headline: '',
    content: '',
    button_text: '',
    button_link: '',
    alignment: 'left'
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient.get('/admin/about-buloke-settings')
      .then(res => { setSettings(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    try {
      await apiClient.put('/admin/about-buloke-settings', settings);
      toast.success('About Buloke section updated!');
    } catch (e) {
      toast.error('Failed to save');
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><p>Loading...</p></div>;

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="bg-white shadow">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <Link to="/admin" className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-4">
            <ArrowLeft size={20} className="mr-2" /> Back to Dashboard
          </Link>
          <h1 className="text-3xl font-bold text-gray-900">About Buloke Section</h1>
          <p className="text-gray-600 mt-1">Edit the About Buloke Realty section on the homepage</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow p-6 space-y-6">
          <div>
            <Label>Headline</Label>
            <Input
              value={settings.headline}
              onChange={e => setSettings({ ...settings, headline: e.target.value })}
              placeholder="About Buloke Realty"
              data-testid="about-buloke-headline"
            />
          </div>

          <div>
            <Label>Content</Label>
            <Textarea
              value={settings.content}
              onChange={e => setSettings({ ...settings, content: e.target.value })}
              placeholder="Section body text..."
              rows={6}
              data-testid="about-buloke-content"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Button Text</Label>
              <Input
                value={settings.button_text}
                onChange={e => setSettings({ ...settings, button_text: e.target.value })}
                placeholder="Learn More About Buloke"
                data-testid="about-buloke-btn-text"
              />
            </div>
            <div>
              <Label>Button Link</Label>
              <Input
                value={settings.button_link}
                onChange={e => setSettings({ ...settings, button_link: e.target.value })}
                placeholder="https://www.buloke.com"
                data-testid="about-buloke-btn-link"
              />
            </div>
          </div>

          <div>
            <Label className="mb-2 block">Text Alignment</Label>
            <div className="flex gap-2">
              {[
                { value: 'left', icon: AlignLeft, label: 'Left' },
                { value: 'center', icon: AlignCenter, label: 'Center' },
                { value: 'right', icon: AlignRight, label: 'Right' },
              ].map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setSettings({ ...settings, alignment: opt.value })}
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-lg border-2 transition-colors ${
                    settings.alignment === opt.value
                      ? 'border-[#E12429] bg-red-50 text-[#E12429]'
                      : 'border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}
                  data-testid={`align-${opt.value}`}
                >
                  <opt.icon size={18} />
                  <span className="font-medium">{opt.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Live Preview */}
          <div className="border rounded-lg p-6 bg-gray-50">
            <p className="text-xs font-semibold text-gray-400 uppercase mb-4">Preview</p>
            <div className={`max-w-2xl ${settings.alignment === 'center' ? 'mx-auto text-center' : settings.alignment === 'right' ? 'ml-auto text-right' : 'text-left'}`}>
              <h3 className="text-2xl font-bold text-[#222] mb-3">{settings.headline || 'Headline'}</h3>
              <p className="text-sm text-[#666] leading-relaxed mb-4">{settings.content || 'Content will appear here...'}</p>
              {settings.button_text && (
                <span className="inline-flex items-center gap-1 bg-[#E12429] text-white text-sm font-semibold px-5 py-2 rounded">
                  {settings.button_text} →
                </span>
              )}
            </div>
          </div>

          <div className="flex justify-end">
            <Button onClick={handleSave} className="bg-blue-600 hover:bg-blue-700 px-8" data-testid="save-about-buloke">
              <Save size={18} className="mr-2" /> Save Changes
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
